import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import Api from "../api/api"
import ErrorDialog from "./widgets/ErrorDialog"
import IlokLinkForm, { ilokLinkMessage } from "./IlokLinkForm"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// ilokEmail is PACE's address for the account — masked while the link is
// pending (the user only typed the ID), in full once confirmed
export type IlokAccount = { ilokId: string, ilokEmail: string, confirmed: boolean, emailSentAt: string | null }

// The Account page's iLok section: lists the iLok.com accounts linked (or
// being linked) to this user and starts the linking workflow for one on a
// DIFFERENT email address — accounts registered to this account's email
// are linked by the backend on the app's first load. The user enters an
// iLok User ID; the backend looks it up on PACE's iLok Remote API and
// either links it on the spot (its email turns out to be this account's
// verified address after all) or emails a verification link to the
// iLok.com address; clicking that link (an API-served page, no login
// required) completes the link. Several iLok accounts per user are fine.
//
// Navigating here with LINK_ILOK_STATE as router state (the Dashboard card's
// and Offers reminders' CTAs) opens the link dialog straight away; the state is cleared on arrival
// so a reload or back-navigation doesn't reopen it.
export const LINK_ILOK_STATE = { linkIlok: true }

export default function IlokAccountsCard({ accessToken }: { accessToken: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<IlokAccount[] | undefined>(undefined)
  const [loadError, setLoadError] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)

  useEffect(() => {
    if ((location.state as { linkIlok?: boolean } | null)?.linkIlok !== true) return
    setLinkOpen(true)
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate])
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [resendNotice, setResendNotice] = useState("")
  const [actionError, setActionError] = useState("")

  const loadAccounts = useCallback(() => {
    Api.getIlokAccounts({ accessToken })
      .then(({ ilokAccounts }) => setAccounts(ilokAccounts))
      .catch(() => setLoadError(true))
  }, [accessToken])

  useEffect(() => {
    // empty on a hard reload of the page before auth resolves
    if (!accessToken) return
    loadAccounts()
  }, [accessToken, loadAccounts])

  // Restarting the workflow IS the resend: the backend re-verifies against
  // PACE, rotates the secret and emails a fresh link (60s server cooldown).
  const resend = async (account: IlokAccount) => {
    setResendingId(account.ilokId)
    setResendNotice("")
    try {
      const result = await Api.linkIlokAccount({ ilokId: account.ilokId, accessToken })
      if (result.alreadyLinked || result.linked) {
        // confirmed in the meantime (or linkable outright now that the
        // addresses match) — just refresh the badge
        loadAccounts()
      } else {
        setResendNotice(`Verification email re-sent to ${result.email}.`)
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "unknown error")
    }
    setResendingId(null)
  }

  // a link whose email never gets clicked would sit pending for good
  const cancel = async (account: IlokAccount) => {
    setResendingId(account.ilokId)
    setResendNotice("")
    try {
      await Api.cancelIlokLink({ ilokId: account.ilokId, accessToken })
      loadAccounts()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "unknown error")
    }
    setResendingId(null)
  }

  return (
    <div className="card" style={{ maxWidth: 480, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="detail-grid">
          <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
            <span className="detail-label">iLok Accounts</span>
          </div>
        </div>
        <button
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
          onClick={() => setLinkOpen(true)}
        >
          Link iLok account
        </button>
      </div>

      {loadError && <p className="empty-state">Error loading iLok accounts</p>}
      {!loadError && accounts === undefined && <p className="empty-state"><Spinner size={16} /> Loading…</p>}
      {accounts !== undefined && accounts.length === 0 && (
        <p className="empty-state">No iLok accounts linked</p>
      )}
      {accounts !== undefined && accounts.map((account) => (
        <div key={account.ilokId} className="license-summary-row">
          <span className="detail-value">{account.ilokId}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!account.confirmed && (
              <button
                className="card-link"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
                onClick={() => resend(account)}
                disabled={resendingId !== null}
              >
                {resendingId === account.ilokId ? <Spinner size={10} /> : "resend email"}
              </button>
            )}
            {!account.confirmed && (
              <button
                className="card-link"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
                onClick={() => cancel(account)}
                disabled={resendingId !== null}
              >
                cancel
              </button>
            )}
            {account.confirmed
              ? <span className="badge">linked</span>
              : <span className="badge badge-trial">pending</span>}
          </span>
        </div>
      ))}

      {resendNotice && <p className="generate-codes-success">{resendNotice}</p>}

      {accounts !== undefined && accounts.some((a) => a.confirmed) && (
        <Link to="/ilok" className="card-link" style={{ fontSize: "10pt" }}>
          View your iLok licenses
        </Link>
      )}

      {actionError && (
        <ErrorDialog message={actionError} onClose={() => setActionError("")} />
      )}

      {linkOpen && (
        <IlokLinkDialog
          accessToken={accessToken}
          onClose={() => setLinkOpen(false)}
          onDone={loadAccounts}
        />
      )}
    </div>
  )
}

function IlokLinkDialog({ accessToken, onClose, onDone }: {
  accessToken: string,
  onClose: () => void,
  onDone: () => void,
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"form" | "success">("form")
  const [busy, setBusy] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => { dialogRef.current?.showModal() }, [])

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy}>&times;</button>
        <h2 className="redeem-dialog-title">Link iLok Account</h2>

        {step === "success" ? (
          <>
            <p className="generate-codes-success">{successMessage}</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <IlokLinkForm
            accessToken={accessToken}
            cancelLabel="Cancel"
            onCancel={() => dialogRef.current?.close()}
            onBusyChange={setBusy}
            onLinked={(result) => {
              setSuccessMessage(ilokLinkMessage(result))
              onDone()
              setStep("success")
            }}
          />
        )}
      </div>
    </dialog>
  )
}
